import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from '@supabase/supabase-js';

import type { PersistedTranscriptInput } from './persistence';
import type { TranscriptSegmentRow } from './realtimeSegment';

export interface LegacyTranscriptRealtimeRow {
  call_id: string;
  content?: PersistedTranscriptInput[] | null;
  normalized_ready?: boolean | null;
}

interface RegisterTranscriptRealtimeOptions {
  channel: RealtimeChannel;
  dbCallId: string;
  mode: TranscriptRealtimeMode;
  onNormalized: (
    payload: RealtimePostgresChangesPayload<TranscriptSegmentRow>,
  ) => void;
  onLegacy: (
    payload: RealtimePostgresChangesPayload<LegacyTranscriptRealtimeRow>,
  ) => void;
}

export type TranscriptRealtimeMode = 'normalized' | 'legacy' | 'dual';

export function legacyEventAuthorityMode(
  row: LegacyTranscriptRealtimeRow | undefined,
): TranscriptRealtimeMode | null {
  if (typeof row?.normalized_ready !== 'boolean') {
    return null;
  }
  return row.normalized_ready ? 'normalized' : 'legacy';
}

/**
 * Resolve per-call authority before binding Realtime. A global gate cannot
 * imply normalized authority because malformed historical calls deliberately
 * remain on the legacy projection. On lookup failure, dual mode favors
 * correctness while avoiding that degraded fallback during healthy operation.
 */
export async function resolveTranscriptRealtimeMode(
  supabase: SupabaseClient,
  dbCallId: string,
  normalizedStorageEnabled: boolean,
): Promise<TranscriptRealtimeMode> {
  const { data, error } = await supabase
    .from('transcripts')
    .select('normalized_ready')
    .eq('call_id', dbCallId)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') {
    const normalizedSchemaMissing = new Set(['42703', '42P01', 'PGRST204'])
      .has(error.code || '');
    return !normalizedStorageEnabled || normalizedSchemaMissing ? 'legacy' : 'dual';
  }
  return data?.normalized_ready === false ? 'legacy' : 'normalized';
}

type PostgresChangesRegistrar = (
  type: 'postgres_changes',
  filter: {
    event: '*' | 'INSERT' | 'UPDATE';
    schema: 'public';
    table: string;
    filter: string;
  },
  callback: (
    payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  ) => void,
) => RealtimeChannel;

/** Register exactly the transcript bindings selected by the reversible gate. */
export function registerTranscriptRealtimeSubscriptions({
  channel,
  dbCallId,
  mode,
  onNormalized,
  onLegacy,
}: RegisterTranscriptRealtimeOptions): void {
  const register = channel.on.bind(channel) as unknown as PostgresChangesRegistrar;
  if (mode !== 'normalized') {
    register(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'transcripts',
        filter: `call_id=eq.${dbCallId}`,
      },
      (payload) => onLegacy(
        payload as RealtimePostgresChangesPayload<LegacyTranscriptRealtimeRow>,
      ),
    );
    if (mode === 'legacy') return;
  } else {
    // Register only the one-time header INSERT while normalized. This closes
    // the no-header lookup race with a direct legacy insert without receiving
    // growing whole-array payloads on every normalized header UPDATE.
    register(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'transcripts',
        filter: `call_id=eq.${dbCallId}`,
      },
      (payload) => onLegacy(
        payload as RealtimePostgresChangesPayload<LegacyTranscriptRealtimeRow>,
      ),
    );
  }

  for (const event of ['INSERT', 'UPDATE'] as const) {
    register(
      'postgres_changes',
      {
        event,
        schema: 'public',
        table: 'transcript_segments',
        filter: `call_id=eq.${dbCallId}`,
      },
      (payload) => onNormalized(
        payload as RealtimePostgresChangesPayload<TranscriptSegmentRow>,
      ),
    );
  }
}
